import { Injectable } from '@angular/core';
import { ReferralMetadata } from '../models/referral-metadata';

const REFERRAL_METADATA_STORAGE_KEY = 'referralMetadata';
const FBCLID_QUERY_KEYS = ['fbclid', 'fbclid_test'] as const;
const GCLID_QUERY_KEYS = ['gclid', 'gclid_test'] as const;

@Injectable({
  providedIn: 'root'
})
export class ReferralMetadataService {
  private metadata: ReferralMetadata | null = null;

  constructor() {
    this.metadata = this.loadFromStorage();
  }

  captureFromUrl(url: string): void {
    if (!url) {
      this.clearMetadata();
      return;
    }

    const parsedMetadata = this.parseFromUrl(url);

    if (!parsedMetadata) {
      this.clearMetadata();
      return;
    }

    this.metadata = parsedMetadata;
    this.saveToStorage(parsedMetadata);
  }

  getMetadata(): ReferralMetadata | null {
    return this.metadata;
  }

  hasMetadata(): boolean {
    return this.metadata !== null;
  }

  clearMetadata(): void {
    this.metadata = null;

    if (typeof globalThis === 'undefined' || !globalThis.window || !globalThis.window.localStorage) {
      return;
    }

    globalThis.window.localStorage.removeItem(REFERRAL_METADATA_STORAGE_KEY);
  }

  private parseFromUrl(url: string): ReferralMetadata | null {
    try {
      const urlInstance = new URL(url);
      const params = urlInstance.searchParams;

      const utmSource = params.get('utm_source') ?? undefined;
      const utmMedium = params.get('utm_medium') ?? undefined;
      const utmCampaign = params.get('utm_campaign') ?? undefined;
      const utmTerm = params.get('utm_term') ?? undefined;
      const utmContent = params.get('utm_content') ?? undefined;
      const utmCreativeId = params.get('utm_creative_id') ?? undefined;
      const fbclid = this.getFirstQueryValue(params, FBCLID_QUERY_KEYS);
      const gclid = this.getFirstQueryValue(params, GCLID_QUERY_KEYS);
      const referralCode = params.get('ref') ?? params.get('referral') ?? undefined;
      const partnerId = params.get('partner_id') ?? params.get('partnerId') ?? undefined;

      const hasAnyMetadata =
        !!utmSource ||
        !!utmMedium ||
        !!utmCampaign ||
        !!utmTerm ||
        !!utmContent ||
        !!utmCreativeId ||
        !!fbclid ||
        !!gclid ||
        !!referralCode ||
        !!partnerId;

      if (!hasAnyMetadata) {
        return null;
      }

      const urlValue = urlInstance.toString();

      const metadata: ReferralMetadata = {
        url: urlValue,
        landingUrl: urlValue,
        utmSource,
        utmMedium,
        utmCampaign,
        utmTerm,
        utmContent,
        utmCreativeId,
        fbclid,
        gclid,
        referralCode,
        partnerId,
        createdAt: new Date().toISOString()
      };

      return metadata;
    } catch {
      return null;
    }
  }

  private loadFromStorage(): ReferralMetadata | null {
    if (typeof globalThis === 'undefined' || !globalThis.window || !globalThis.window.localStorage) {
      return null;
    }

    const storedValue = globalThis.window.localStorage.getItem(REFERRAL_METADATA_STORAGE_KEY);

    if (!storedValue) {
      return null;
    }

    try {
      const parsed = JSON.parse(storedValue) as ReferralMetadata;
      return parsed;
    } catch {
      return null;
    }
  }

  private saveToStorage(metadata: ReferralMetadata): void {
    if (typeof globalThis === 'undefined' || !globalThis.window || !globalThis.window.localStorage) {
      return;
    }

    globalThis.window.localStorage.setItem(REFERRAL_METADATA_STORAGE_KEY, JSON.stringify(metadata));
  }

  private getFirstQueryValue(
    params: URLSearchParams,
    keys: readonly string[]
  ): string | undefined {
    for (const key of keys) {
      const value = params.get(key);
      if (value) {
        return value;
      }
    }

    return undefined;
  }
}

